import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable } from 'rxjs/Observable';
import {
  SubscriptionPlan,
  SubscriptionPlanItem,
  SubscriptionPlanDetail,
  SubscriptionPlanStatus
} from '../models/subscriptionplan';
import {environment} from '../../environments/environment';
import {checkIfTruncated, getFileNameFromResponseContentDisposition, saveFile} from './utils/file-download-helper';
import { TripPeriodPrice } from '../models/trip-period-price';

@Injectable()
export class SubscriptionplanService {

  SUBSCRIPTION_PLANS_API_URL: string = environment.serverUrl + 'api/1/subscriptionplans';

  constructor(private httpClient: HttpClient) {
  }

  /**
   * Subscription plans for every group in which the user has admin access
   *
   */
  loadVisibleSubscriptionPlans(): Observable<SubscriptionPlan[]> {
    return this.httpClient.get<SubscriptionPlan[]>(this.SUBSCRIPTION_PLANS_API_URL);
  }

  loadSubscriptionPlansByGroup(groupId: number): Observable<SubscriptionPlan[]> {
    return this.httpClient.get<SubscriptionPlan[]>(this.SUBSCRIPTION_PLANS_API_URL + '?groupId=' + groupId);
  }

  getPaginatedFilteredSortedSP(pageIndex: number,
    itemPerPage: number,
    filters: Map<string, any>,
    criterias: Map<string, boolean>): Observable<any> {
    const url = this.getFormattedURL(pageIndex, itemPerPage, criterias, filters);
    return this.httpClient.get<any>(encodeURI(url), {observe: 'response'}).map(response => {
      return {
        headers: response.headers,
        res: <SubscriptionPlan[]>response.body
      };
    });
  }

  downloadXLSFile(pageIndex: number, itemPerPage: number, filters: Map<string, any>, criterias: Map<string, boolean>): any {
    const url = this.getFormattedURL(pageIndex, itemPerPage, criterias, filters);
    return this.httpClient.get<any>(encodeURI(url), {
      headers: {Accept: 'application/octet-stream'},
      responseType: 'blob' as 'json',
      observe: 'response'
    }).map((response) => {
      const fileName = getFileNameFromResponseContentDisposition(response, 'subscriptionplans.xls');
      const isTruncated = checkIfTruncated(response);
      saveFile(response.body.slice(), fileName);
      return isTruncated;
    });
  }

  private getFormattedURL(pageIndex: number, itemPerPage: number, criterias: Map<string, boolean>, filters: Map<string, any>) {
    const offset = ((pageIndex - 1) * itemPerPage); // records offset to display the specific range
    let url = this.SUBSCRIPTION_PLANS_API_URL + '?pagination=' + offset + '-' + itemPerPage + '&sorts=';

    if (criterias.size === 0) { // if no sorts criterias => sort by id
      url += 's.name asc';
    } else {
      Array.from(criterias.keys()).forEach(c => url += c + (criterias.get(c) === true ? ' asc' : ' desc') + ',');
      url = url.substring(0, url.length - 1);
    }

    if (filters !== undefined) {

      const filtersAsString = JSON.stringify(filters, (key, value) => {
        if (value !== null && value !== undefined && value !== '') { return value; }
      });

      if (filtersAsString.length > 2) {
        url += '&filters=' + filtersAsString;
      }
    }
    return url;
  }

  get(sId: number): Observable<SubscriptionPlanDetail> {
    return this.httpClient.get<SubscriptionPlanDetail>(encodeURI(this.SUBSCRIPTION_PLANS_API_URL + '/' + sId));
  }

  post(s: SubscriptionPlanDetail, createForAllGroups: boolean): Observable<SubscriptionPlanDetail> {
    const url = this.SUBSCRIPTION_PLANS_API_URL + (createForAllGroups ? '?allGroups=true' : '');
    return this.httpClient.post<SubscriptionPlanDetail>(url, s);
  }

  put(s: SubscriptionPlanDetail): Observable<SubscriptionPlanDetail> {
    return this.httpClient.put<SubscriptionPlanDetail>(this.SUBSCRIPTION_PLANS_API_URL + '/' + s.id, s);
  }

  delete(sp: SubscriptionPlanItem): Observable<any> {
    return this.httpClient.delete(encodeURI(this.SUBSCRIPTION_PLANS_API_URL + '/' + sp.planId));
  }

  patch(sp: SubscriptionPlanItem): Observable<any> {
    const s = new SubscriptionPlanItem;
    s.status = SubscriptionPlanStatus.INACTIVE;
    return this.httpClient.patch(encodeURI(this.SUBSCRIPTION_PLANS_API_URL + '/' + sp.planId), s);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/subscriptionplan.service.ts